import React, { useEffect, useState } from 'react';
import { CsrPager } from '../CsrRegister';
import { CsrInlineError } from '../CsrStatus';
import { useCsrPagedList } from '../../../hooks/useCsrDashboard';
import { fetchCsrFarmers, fetchCsrSoilDidis, fetchCsrSoilTest } from '../../../services/dashboardApi';
import { DASH, errorMessage, fmt, fmtDate, initials, orDash, titleCase } from '../csrFormat';
import { displayValue } from '../csrSoilParameters';

const SoilDidiCards = ({ didis, status, error, onRetry, selectedId, onSelect, scopeName, livelihoods }) => (
  <section aria-labelledby="csr-dd-h" className="csr-card csr-didis">
    <div className="csr-didis__head">
      <div className="d-flex flex-column" style={{ gap: 2, maxWidth: 760 }}>
        <h2 id="csr-dd-h">Soil Didis</h2>
        <p className="csr-panel__hint">Each Soil Didi tests soil, explains results to farmers and supports them through the season. Select one to see the farmers they support.</p>
      </div>
      {status === 'success' ? (
        <span className="csr-didis__count">{`${fmt(didis.length)} shown · ${livelihoods} livelihoods in ${scopeName}`}</span>
      ) : null}
    </div>

    {status === 'error' ? <CsrInlineError message={error} onRetry={onRetry} /> : null}
    {status === 'loading' ? (
      <div className="csr-didis__grid">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="csr-skel" style={{ height: 112 }} />)}</div>
    ) : null}
    {status === 'success' && didis.length === 0 ? <p className="csr-register__empty">No Soil Didis recorded for this partner yet.</p> : null}
    {status === 'success' && didis.length ? (
      <div className="csr-didis__grid">
        {didis.map((d) => {
          const on = selectedId === d.field_officer_id;
          return (
            <button key={d.field_officer_id} type="button" className="csr-didi" aria-pressed={on} onClick={() => onSelect(on ? null : d.field_officer_id)}>
              <span className="csr-didi__who">
                <span className="csr-didi__ini" aria-hidden="true">{initials(d.name)}</span>
                <span className="d-flex flex-column">
                  <strong>{d.name}</strong>
                  <span className="csr-row__sub">{`${[d.city, d.state].filter(Boolean).join(', ') || DASH} · ${fmt(d.farmers)} farmers`}</span>
                </span>
              </span>
              <span className="csr-didi__stats">
                <span><strong>{fmt(d.soil_tests)}</strong><span>tests</span></span>
                <span><strong>{DASH}</strong><span>field visits</span></span>
                <span><strong>{DASH}</strong><span>earned</span></span>
              </span>
            </button>
          );
        })}
      </div>
    ) : null}
  </section>
);

const Tile = ({ label, value, note }) => (
  <div className="csr-journey__tile">
    <span>{label}</span>
    <strong>{value}</strong>
    {note ? <span>{note}</span> : null}
  </div>
);

const FarmerProfile = ({ farmer, detail, score, onOpenTest, onOpenFarmer }) => {
  const p = detail?.parameters || null;
  return (
    <article className="csr-journey" aria-live="polite">
      <div className="csr-journey__head">
        <div className="d-flex flex-column" style={{ gap: 2 }}>
          <h3>{`Farmer ${farmer.user_id}`}</h3>
          <span className="csr-row__sub">
            {`${[farmer.village, farmer.district].filter(Boolean).join(', ') || DASH} · Soil Didi: ${orDash(farmer.field_officer_name)} · ${orDash(titleCase(farmer.latest_crop))}`}
          </span>
        </div>
        <span className="csr-chip">{`${fmt(farmer.soil_tests)} soil tests`}</span>
      </div>
      <div className="csr-journey__tiles">
        <Tile
          label="Soil"
          value={<>{`${score.before} → ${score.after}`} <span className="csr-row__sub">score</span></>}
          note={`Organic carbon ${p ? displayValue(p.oc) : DASH} % · pH ${p ? displayValue(p.ph) : DASH}`}
        />
        <Tile label="Satellite" value={<>{`${DASH} → ${DASH}`} <span className="csr-row__sub">plot NDVI</span></>} note="Same fortnight, last season vs this season" />
        <Tile label={`Last field visit · ${DASH}`} value={DASH} note="Field visits are not recorded yet" />
      </div>
      <div className="csr-journey__facts">
        <Tile label="Fertilizer bags / acre" value={DASH} />
        <Tile label="Estimated saving / acre" value={DASH} />
        <Tile label="Test location" value={orDash(detail?.farm_location)} />
      </div>
      <div className="csr-journey__advice">
        <strong>Latest crop advice</strong>
        <span>{DASH}</span>
      </div>
      <div className="d-flex flex-wrap" style={{ gap: 8 }}>
        {farmer.latest_sample_id ? (
          <button type="button" className="csr-btn csr-btn--ghost" onClick={() => onOpenTest(farmer.latest_sample_id)}>{`Soil test ${farmer.latest_sample_id} · ${fmtDate(farmer.latest_sampled_on)} →`}</button>
        ) : null}
        <button type="button" className="csr-btn csr-btn--ghost" onClick={() => onOpenFarmer(farmer.user_id)}>Farmer record →</button>
      </div>
    </article>
  );
};

const FarmerJourneys = ({ list, selected, onSelect, detail, score, didiName, onClearDidi, onOpenTest, onOpenFarmer }) => (
  <section aria-labelledby="csr-fp-h" className="csr-card csr-didis">
    <div className="csr-didis__head">
      <div className="d-flex flex-column" style={{ gap: 2 }}>
        <h2 id="csr-fp-h">Farmer journeys</h2>
        <p className="csr-panel__hint">{didiName ? `Farmers supported by ${didiName}` : 'Soil, satellite and field-visit data for each farmer'}</p>
      </div>
      {didiName ? <button type="button" className="csr-btn csr-btn--ghost" onClick={onClearDidi}>Show all farmers</button> : null}
    </div>

    {list.status === 'error' ? <CsrInlineError message={list.error} onRetry={list.retry} /> : (
      <div className="csr-journeys">
        <div className="csr-journeys__list">
          {list.status === 'loading' && !list.rows.length
            ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="csr-skel" style={{ height: 56 }} />)
            : list.rows.map((f) => (
              <button key={f.user_id} type="button" className="csr-journeys__item" aria-pressed={selected?.user_id === f.user_id} onClick={() => onSelect(f.user_id)}>
                <strong>{f.latest_sample_id || `Farmer ${f.user_id}`}</strong>
                <span>{[f.village || f.district, titleCase(f.latest_crop)].filter(Boolean).join(' · ') || DASH}</span>
              </button>
            ))}
          {list.status === 'success' && !list.rows.length ? <p className="csr-register__empty">No farmers to show.</p> : null}
          {list.total > list.limit ? (
            <CsrPager page={list.page} limit={list.limit} total={list.total} onPage={list.setPage} disabled={list.status === 'loading'} />
          ) : null}
        </div>
        {selected ? (
          <FarmerProfile farmer={selected} detail={detail} score={score} onOpenTest={onOpenTest} onOpenFarmer={onOpenFarmer} />
        ) : null}
      </div>
    )}
  </section>
);

const SoilDidisTab = ({ adminId, state, scopeName, livelihoods, score, onOpenTest, onOpenFarmer }) => {
  const [didis, setDidis] = useState({ status: 'loading', rows: [], error: '' });
  const [attempt, setAttempt] = useState(0);
  const [didiId, setDidiId] = useState(null);
  const [farmerId, setFarmerId] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let ignore = false;
    setDidis((d) => ({ ...d, status: 'loading' }));
    fetchCsrSoilDidis(adminId, { state })
      .then((data) => { if (!ignore) setDidis({ status: 'success', rows: data?.rows || [], error: '' }); })
      .catch((err) => { if (!ignore) setDidis({ status: 'error', rows: [], error: errorMessage(err, 'Could not load Soil Didis.') }); });
    return () => { ignore = true; };
  }, [adminId, state, attempt]);

  const list = useCsrPagedList(fetchCsrFarmers, adminId, { state, field_officer_id: didiId }, { limit: 8 });
  const selected = list.rows.find((f) => f.user_id === farmerId) || list.rows[0] || null;
  const latestSample = selected?.latest_sample_id || null;

  useEffect(() => {
    setDetail(null);
    if (!latestSample) return undefined;
    let ignore = false;
    fetchCsrSoilTest(adminId, latestSample)
      .then((data) => { if (!ignore) setDetail(data?.test || null); })
      .catch(() => { if (!ignore) setDetail(null); });
    return () => { ignore = true; };
  }, [adminId, latestSample]);

  const didiName = didis.rows.find((d) => d.field_officer_id === didiId)?.name;

  return (
    <div className="d-flex flex-column" style={{ gap: 20 }}>
      <SoilDidiCards
        didis={didis.rows}
        status={didis.status}
        error={didis.error}
        onRetry={() => setAttempt((a) => a + 1)}
        selectedId={didiId}
        onSelect={(id) => { setDidiId(id); setFarmerId(null); }}
        scopeName={scopeName}
        livelihoods={livelihoods}
      />
      <FarmerJourneys
        list={list}
        selected={selected}
        onSelect={setFarmerId}
        detail={detail}
        score={score}
        didiName={didiName}
        onClearDidi={() => { setDidiId(null); setFarmerId(null); }}
        onOpenTest={onOpenTest}
        onOpenFarmer={onOpenFarmer}
      />
    </div>
  );
};

export default SoilDidisTab;
